import { useState, useRef, useEffect } from 'react'
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  ShoppingBag,
  User,
  Package,
  MapPin,
  Search,
  LogOut,
  ChevronDown,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  Clock,
  HeartHandshake,
  CheckCircle2,
  X,
  Edit3,
  Home,
  LayoutGrid,
} from 'lucide-react'
import { useCart } from '../state/cart'
import { useSession } from '../state/session'
import { useTranslation } from '../i18n/LanguageContext'
import { LanguageSelector } from './LanguageSelector'
import { api } from '../api/client'

export function Layout() {
  const { itemCount, subtotalLabel } = useCart()
  const { signedIn, phoneNumber, fullName, email, updateProfile, signOut } = useSession()
  const { t, isHindi } = useTranslation()
  const [profileOpen, setProfileOpen] = useState(false)
  const [editProfileOpen, setEditProfileOpen] = useState(false)
  const [pincodeOpen, setPincodeOpen] = useState(false)
  const [pincode, setPincode] = useState('400001')
  const [pincodeStatus, setPincodeStatus] = useState<string | null>(null)
  const [pincodeLoading, setPincodeLoading] = useState(false)
  const [headerSearch, setHeaderSearch] = useState('')

  // Edit profile form state
  const [nameInput, setNameInput] = useState('')
  const [emailInput, setEmailInput] = useState('')
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editSuccess, setEditSuccess] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Sync edit profile form
  useEffect(() => {
    setNameInput(fullName || '')
    setEmailInput(email || '')
  }, [fullName, email, editProfileOpen])

  async function handleCheckPincode(e: React.FormEvent) {
    e.preventDefault()
    if (!pincode.trim()) return
    setPincodeLoading(true)
    setPincodeStatus(null)
    try {
      const res = await api.serviceability.check(pincode.trim())
      if (res.deliverable) {
        setPincodeStatus(t('pincode_available', { area: res.areaName || '' }))
      } else {
        setPincodeStatus(t('pincode_outside'))
      }
    } catch {
      setPincodeStatus(t('pincode_standard'))
    } finally {
      setPincodeLoading(false)
    }
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault()
    setEditBusy(true)
    setEditError(null)
    setEditSuccess(false)
    try {
      const profilePayload: { fullName?: string; email?: string } = {}
      if (nameInput.trim()) profilePayload.fullName = nameInput.trim()
      if (emailInput.trim()) profilePayload.email = emailInput.trim()
      await updateProfile(profilePayload)
      setEditSuccess(true)
      setTimeout(() => {
        setEditProfileOpen(false)
        setEditSuccess(false)
      }, 1200)
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Could not save profile.')
    } finally {
      setEditBusy(false)
    }
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (headerSearch.trim().length > 0) {
      navigate(`/?q=${encodeURIComponent(headerSearch.trim())}`)
    }
  }

  const displayName = fullName || (phoneNumber ? `Member (${phoneNumber.slice(-4)})` : 'Customer')
  const initial = (fullName ? fullName.charAt(0) : phoneNumber ? phoneNumber.slice(-1) : 'U').toUpperCase()

  return (
    <div className="flex min-h-screen flex-col bg-stone-50/70 text-stone-900">
      <a href="#main" className="sr-only focus:not-sr-only focus:p-3">
        Skip to content
      </a>

      {/* Top Banner Ribbon */}
      <div className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 px-3 py-1.5 text-center text-[11px] sm:text-xs font-medium text-emerald-100 overflow-hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2">
          <div className="flex flex-1 items-center justify-center sm:justify-start gap-3 truncate">
            <span className="flex items-center gap-1 shrink-0">
              <Clock className="h-3.5 w-3.5 text-emerald-300" />
              <span>{t('ribbon_delivery_speed')}</span>
            </span>
            <span className="hidden sm:inline text-emerald-400/60">•</span>
            <span className="hidden sm:flex items-center gap-1 shrink-0">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              <span>{t('ribbon_free_delivery')}</span>
            </span>
            <span className="hidden md:inline text-emerald-400/60">•</span>
            <span className="hidden md:flex items-center gap-1 shrink-0">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
              <span>{t('ribbon_guarantee')}</span>
            </span>
          </div>

          {/* Quick Language Switcher in Ribbon on desktop */}
          <div className="hidden sm:flex items-center shrink-0">
            <LanguageSelector variant="ribbon" />
          </div>
        </div>
      </div>

      {/* Main Glass Header */}
      <header className="glass-header shadow-[0_4px_20px_-4px_rgba(0,0,0,0.03)] w-full">
        <div className="mx-auto max-w-6xl px-3 py-2.5 sm:px-4 sm:py-3">
          {/* Top Row: Logo, Location, Language & Profile/Sign In */}
          <div className="flex items-center justify-between gap-1.5 sm:gap-6 min-w-0 w-full">
            {/* Brand Logo */}
            <Link to="/" className="group flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-700 to-emerald-500 text-white shadow-md shadow-emerald-700/20 transition-transform group-hover:scale-105">
                <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="leading-tight">
                <span className="font-heading text-base font-extrabold tracking-tight text-emerald-950 sm:text-2xl">
                  Rojana<span className="text-emerald-600">Mart</span>
                </span>
                <span className="hidden sm:block text-[10px] font-bold tracking-wider uppercase text-emerald-700">
                  {t('brand_superfast')}
                </span>
              </div>
            </Link>

            {/* Delivery Location Pill */}
            <button
              type="button"
              onClick={() => setPincodeOpen(true)}
              className="flex items-center gap-1 sm:gap-1.5 rounded-xl border border-stone-200/80 bg-stone-50/90 px-1.5 py-1 sm:px-3 sm:py-1.5 text-left text-[11px] sm:text-xs transition-colors hover:border-emerald-300 hover:bg-emerald-50/40 shrink-0 max-w-[80px] sm:max-w-none"
              title="Change Delivery Location"
            >
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600 shrink-0" />
              <div className="leading-tight truncate">
                <div className="hidden sm:block font-bold text-stone-900">{t('nav_deliver_in')}</div>
                <div className="font-bold sm:font-normal text-stone-700 sm:text-stone-500 truncate">
                  {pincode} <span className="text-emerald-700 font-bold">▾</span>
                </div>
              </div>
            </button>

            {/* Desktop Search Bar */}
            <form
              onSubmit={handleSearchSubmit}
              className="relative hidden flex-1 max-w-md sm:block"
            >
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <input
                type="search"
                placeholder={t('nav_search_placeholder')}
                value={headerSearch}
                onChange={(e) => setHeaderSearch(e.target.value)}
                className="w-full rounded-xl border border-stone-200 bg-stone-100/70 py-2 pl-9 pr-8 text-xs text-stone-800 placeholder:text-stone-400 transition-all focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
              />
              {headerSearch && (
                <button
                  type="button"
                  onClick={() => setHeaderSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </form>

            {/* Right Controls */}
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              {/* Language Selector: Compact toggle on mobile */}
              <div className="sm:hidden">
                <LanguageSelector variant="compact" />
              </div>

              {/* Language Selector: header variant on tablet/desktop */}
              <div className="hidden sm:block md:hidden">
                <LanguageSelector variant="header" />
              </div>

              {/* Orders Link (desktop only, mobile has bottom app bar) */}
              <NavLink
                to="/orders"
                className={({ isActive }) =>
                  `hidden sm:flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-900'
                      : 'text-stone-700 hover:bg-stone-100 hover:text-stone-900'
                  }`
                }
              >
                <Package className="h-4 w-4 text-stone-500" />
                <span>{t('nav_orders')}</span>
              </NavLink>

              {/* Cart / Basket button (desktop only, mobile has floating cart + bottom nav) */}
              <Link
                to="/cart"
                className={`hidden sm:flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold transition-all shadow-sm ${
                  itemCount > 0
                    ? 'bg-emerald-700 text-white hover:bg-emerald-800 shadow-emerald-700/20'
                    : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                }`}
              >
                <div className="relative">
                  <ShoppingBag className="h-4 w-4" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-black text-amber-950 shadow-sm animate-pulse">
                      {itemCount}
                    </span>
                  )}
                </div>
                <div className="flex flex-col text-left leading-none">
                  <span className="text-[11px] font-medium">
                    {itemCount > 0 ? t('nav_items_count', { count: itemCount }) : t('nav_basket')}
                  </span>
                  {itemCount > 0 && (
                    <span className="font-bold text-[12px]">{subtotalLabel}</span>
                  )}
                </div>
              </Link>

              {/* User Profile / Sign in */}
              {signedIn ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-1 sm:gap-2 rounded-xl border border-stone-200/90 bg-white p-1 sm:p-1.5 sm:pr-2.5 text-xs font-semibold text-stone-800 shadow-sm transition-all hover:border-emerald-300 hover:bg-emerald-50/50"
                    aria-expanded={profileOpen}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 font-bold text-emerald-800 text-xs">
                      {initial}
                    </div>
                    <span className="max-w-[100px] truncate text-left hidden sm:inline-block">
                      {displayName}
                    </span>
                    <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-stone-400" />
                  </button>

                  {/* Profile Dropdown Menu */}
                  {profileOpen && (
                    <div className="absolute right-0 mt-2 w-64 origin-top-right rounded-2xl border border-stone-200/90 bg-white p-2 shadow-2xl shadow-stone-900/10 ring-1 ring-black/5 z-50">
                      <div className="border-b border-stone-100 px-3 py-2.5">
                        <p className="text-xs font-semibold text-stone-900">{displayName}</p>
                        {phoneNumber && (
                          <p className="text-[11px] font-mono text-stone-500">{phoneNumber}</p>
                        )}
                        {email && (
                          <p className="truncate text-[11px] text-stone-400">{email}</p>
                        )}
                        <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> {t('nav_verified_customer')}
                        </div>
                      </div>

                      <div className="py-1 text-xs">
                        <div className="px-3 py-2 border-b border-stone-100 mb-1 flex items-center justify-between">
                          <span className="text-stone-500">{t('lang_select')}</span>
                          <LanguageSelector variant="header" />
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setProfileOpen(false)
                            setEditProfileOpen(true)
                          }}
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-stone-700 hover:bg-emerald-50 hover:text-emerald-900"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-stone-400" />
                          <span>{t('nav_edit_profile')}</span>
                        </button>

                        <Link
                          to="/orders"
                          onClick={() => setProfileOpen(false)}
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-stone-700 hover:bg-emerald-50 hover:text-emerald-900"
                        >
                          <Package className="h-3.5 w-3.5 text-stone-400" />
                          <span>{t('nav_my_orders')}</span>
                        </Link>

                        <Link
                          to="/checkout"
                          onClick={() => setProfileOpen(false)}
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-stone-700 hover:bg-emerald-50 hover:text-emerald-900"
                        >
                          <MapPin className="h-3.5 w-3.5 text-stone-400" />
                          <span>{t('nav_addresses')}</span>
                        </Link>
                      </div>

                      <div className="border-t border-stone-100 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setProfileOpen(false)
                            void signOut()
                          }}
                          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="h-3.5 w-3.5 text-red-500" />
                          <span>{t('nav_sign_out')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/sign-in"
                  state={{ from: location.pathname }}
                  className="inline-flex items-center justify-center gap-1 sm:gap-1.5 rounded-xl bg-emerald-600 sm:bg-white sm:border sm:border-stone-200 px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-bold text-white sm:text-stone-800 shadow-sm hover:bg-emerald-700 sm:hover:border-emerald-300 transition-all shrink-0"
                >
                  <User className="h-3.5 w-3.5 text-white sm:text-emerald-700" />
                  <span className="hidden xs:inline">{t('nav_sign_in')}</span>
                </Link>
              )}
            </div>
          </div>

          {/* Row 2: Mobile Search Bar (Full width prominent input on mobile) */}
          <form
            onSubmit={handleSearchSubmit}
            className="relative mt-2 sm:hidden"
          >
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="search"
              placeholder={t('nav_search_placeholder')}
              value={headerSearch}
              onChange={(e) => setHeaderSearch(e.target.value)}
              className="w-full rounded-xl border border-stone-200/90 bg-stone-100/90 py-2 pl-9 pr-8 text-xs text-stone-800 placeholder:text-stone-400 transition-all focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
            />
            {headerSearch && (
              <button
                type="button"
                onClick={() => setHeaderSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>
        </div>
      </header>

      {/* Main Container */}
      <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-4 sm:py-6 pb-36 sm:pb-8 overflow-x-hidden max-w-full">
        <Outlet />
      </main>

      {/* Edit Profile Modal */}
      {editProfileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4 backdrop-blur-sm">
          <div className="card w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
              <h2 className="text-base font-bold text-stone-900">{t('profile_title')}</h2>
              <button
                type="button"
                onClick={() => setEditProfileOpen(false)}
                className="rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSaveProfile} className="mt-4 space-y-4">
              {editError && (
                <p className="rounded-xl bg-red-50 p-3 text-xs text-red-700">{editError}</p>
              )}
              {editSuccess && (
                <p className="rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-700">
                  {t('profile_saved')}
                </p>
              )}

              <div>
                <label className="label">{t('profile_mobile')}</label>
                <input
                  type="text"
                  disabled
                  value={phoneNumber || ''}
                  className="field bg-stone-100 text-stone-500 cursor-not-allowed"
                />
                <span className="text-[11px] text-stone-400">{t('profile_mobile_note')}</span>
              </div>

              <div>
                <label className="label">{t('profile_fullname')}</label>
                <input
                  type="text"
                  placeholder={t('profile_fullname_placeholder')}
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  className="field"
                />
              </div>

              <div>
                <label className="label">{t('profile_email')}</label>
                <input
                  type="email"
                  placeholder={t('profile_email_placeholder')}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="field"
                />
                <span className="text-[11px] text-stone-400">{t('profile_email_note')}</span>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditProfileOpen(false)}
                  className="btn-secondary flex-1"
                >
                  {t('profile_cancel')}
                </button>
                <button
                  type="submit"
                  disabled={editBusy}
                  className="btn-primary flex-1"
                >
                  {editBusy ? t('profile_saving') : t('profile_save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pincode / Location Checker Modal */}
      {pincodeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4 backdrop-blur-sm">
          <div className="card w-full max-w-sm p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
              <h2 className="text-base font-bold text-stone-900">{t('pincode_title')}</h2>
              <button
                type="button"
                onClick={() => setPincodeOpen(false)}
                className="rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCheckPincode} className="mt-4 space-y-3">
              <p className="text-xs text-stone-600">
                {t('pincode_desc')}
              </p>

              <div>
                <label className="label">{t('pincode_label')}</label>
                <input
                  type="text"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 400001"
                  className="field font-mono text-base font-bold"
                  autoFocus
                />
              </div>

              {pincodeStatus && (
                <div
                  className={`rounded-xl p-3 text-xs font-semibold ${
                    pincodeStatus.includes('Available') || pincodeStatus.includes('उपलब्ध')
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'bg-amber-50 text-amber-900'
                  }`}
                >
                  {pincodeStatus}
                </div>
              )}

              <button
                type="submit"
                disabled={pincodeLoading || pincode.length < 6}
                className="btn-primary w-full"
              >
                {pincodeLoading ? t('pincode_btn_checking') : t('pincode_btn_check')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="mt-auto border-t border-stone-200/80 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-700 text-white">
                  <ShoppingBag className="h-4 w-4" />
                </div>
                <span className="font-heading text-lg font-bold text-stone-900">RojanaMart</span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-stone-500">
                {t('footer_about')}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-800 font-semibold">
                <Clock className="h-4 w-4" /> {t('footer_hours')}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-900">{t('footer_categories_title')}</h3>
              <ul className="mt-2.5 space-y-1.5 text-xs text-stone-600">
                <li><Link to="/c/vegetables-fruits" className="hover:text-emerald-700">{t('footer_cat_veg_fruit')}</Link></li>
                <li><Link to="/c/dairy-bread-eggs" className="hover:text-emerald-700">{t('footer_cat_dairy')}</Link></li>
                <li><Link to="/c/atta-rice-dal" className="hover:text-emerald-700">{t('footer_cat_staples')}</Link></li>
                <li><Link to="/c/snacks-beverages" className="hover:text-emerald-700">{t('footer_cat_snacks')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-900">{t('footer_customer_care')}</h3>
              <ul className="mt-2.5 space-y-1.5 text-xs text-stone-600">
                <li><Link to="/orders" className="hover:text-emerald-700">{t('footer_care_track')}</Link></li>
                <li><Link to="/checkout" className="hover:text-emerald-700">{t('footer_care_delivery')}</Link></li>
                <li><span className="text-stone-400">{t('footer_care_returns')}</span></li>
                <li><span className="text-stone-400">{t('footer_care_support')}</span></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-900">{t('footer_safe_certified')}</h3>
              <div className="mt-2.5 space-y-2 text-xs text-stone-500">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  <span>{t('footer_cert_fssai')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <HeartHandshake className="h-4 w-4 text-emerald-600" />
                  <span>{t('footer_cert_sanitized')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>{t('footer_cert_gst')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-stone-100 pt-6 text-xs text-stone-400 sm:flex-row">
            <p>{t('footer_copyright', { year: new Date().getFullYear() })}</p>
            <p className="flex items-center gap-3">
              <span>{t('footer_payments_accepted')}</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Bottom Cart Bar for Mobile when cart has items */}
      {itemCount > 0 && location.pathname !== '/cart' && location.pathname !== '/checkout' && (
        <div
          className="fixed left-3 right-3 z-40 sm:hidden animate-in fade-in slide-in-from-bottom-3 duration-200"
          style={{
            bottom: 'calc(68px + max(14px, env(safe-area-inset-bottom, 14px)))',
          }}
        >
          <Link
            to="/cart"
            className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800 p-3 text-white shadow-xl shadow-emerald-950/30 border border-emerald-600/40 active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3">
              <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                <ShoppingBag className="h-5 w-5 text-white" />
                <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-black text-amber-950 shadow-sm animate-pulse">
                  {itemCount}
                </span>
              </div>
              <div className="text-left">
                <p className="text-[11px] font-semibold text-emerald-100">
                  {t('nav_items_count', { count: itemCount })}
                </p>
                <p className="text-sm font-black text-white leading-none mt-0.5">{subtotalLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-black text-emerald-900 shadow-sm">
              <span>{t('nav_basket')}</span>
              <ChevronRight className="h-3.5 w-3.5 text-emerald-800" />
            </div>
          </Link>
        </div>
      )}

      {/* Mobile Bottom App Navigation Bar (Elevated Floating Dock) */}
      <nav
        className="fixed left-3 right-3 z-40 flex items-center justify-around rounded-2xl border border-stone-200/90 bg-white/95 px-1.5 py-1.5 shadow-2xl shadow-stone-900/15 backdrop-blur-xl sm:hidden"
        style={{
          bottom: 'max(14px, env(safe-area-inset-bottom, 14px))',
        }}
        aria-label="Mobile Navigation"
      >
        <NavLink
          to="/"
          className={({ isActive }) =>
            `flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
              isActive ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
            }`
          }
        >
          <Home className="h-5 w-5" />
          <span className="mt-0.5">{isHindi ? 'होम' : 'Home'}</span>
        </NavLink>

        <NavLink
          to="/c/vegetables-fruits"
          className={({ isActive }) =>
            `flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
              isActive ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
            }`
          }
        >
          <LayoutGrid className="h-5 w-5" />
          <span className="mt-0.5">{isHindi ? 'श्रेणियां' : 'Categories'}</span>
        </NavLink>

        <NavLink
          to="/orders"
          className={({ isActive }) =>
            `flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
              isActive ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
            }`
          }
        >
          <Package className="h-5 w-5" />
          <span className="mt-0.5">{isHindi ? 'ऑर्डर्स' : 'Orders'}</span>
        </NavLink>

        <NavLink
          to="/cart"
          className={({ isActive }) =>
            `relative flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
              isActive ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
            }`
          }
        >
          <div className="relative">
            <ShoppingBag className="h-5 w-5" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-2 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-emerald-600 px-0.5 text-[9px] font-black text-white shadow-xs">
                {itemCount}
              </span>
            )}
          </div>
          <span className="mt-0.5">{isHindi ? 'टोकरी' : 'Cart'}</span>
        </NavLink>

        {signedIn ? (
          <button
            type="button"
            onClick={() => setProfileOpen(!profileOpen)}
            className={`flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
              profileOpen ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-extrabold text-emerald-800">
              {initial}
            </div>
            <span className="mt-0.5">{isHindi ? 'प्रोफ़ाइल' : 'Account'}</span>
          </button>
        ) : (
          <NavLink
            to="/sign-in"
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center py-1.5 px-1 text-[10px] font-bold rounded-xl transition-all active:scale-95 ${
                isActive ? 'text-emerald-700 bg-emerald-50/90 font-black shadow-xs' : 'text-stone-500 hover:text-stone-800'
              }`
            }
          >
            <User className="h-5 w-5" />
            <span className="mt-0.5">{isHindi ? 'साइन इन' : 'Sign In'}</span>
          </NavLink>
        )}
      </nav>

      {/* Mobile Profile Bottom Sheet Modal */}
      {profileOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-stone-900/50 backdrop-blur-xs sm:hidden animate-in fade-in duration-200">
          <div className="flex-1" onClick={() => setProfileOpen(false)} />

          <div
            className="rounded-t-3xl border-t border-stone-200 bg-white p-5 shadow-2xl animate-in slide-in-from-bottom duration-200"
            style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))' }}
          >
            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-base font-extrabold text-emerald-800 shadow-sm">
                  {initial}
                </div>
                <div>
                  <h2 className="text-sm font-extrabold text-stone-900">{displayName}</h2>
                  {phoneNumber && (
                    <p className="text-xs font-mono text-stone-500">{phoneNumber}</p>
                  )}
                  {email && (
                    <p className="text-[11px] text-stone-400">{email}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                className="rounded-xl bg-stone-100 p-2 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 space-y-1.5 text-xs font-semibold">
              <div className="flex items-center justify-between rounded-xl px-3 py-2 bg-stone-50 text-stone-700">
                <span className="text-xs font-bold text-stone-500">{t('lang_select')}</span>
                <LanguageSelector variant="compact" />
              </div>

              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false)
                  setEditProfileOpen(true)
                }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-stone-700 hover:bg-emerald-50 active:bg-emerald-50"
              >
                <Edit3 className="h-4 w-4 text-emerald-700" />
                <span>{t('nav_edit_profile')}</span>
              </button>

              <Link
                to="/orders"
                onClick={() => setProfileOpen(false)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-stone-700 hover:bg-emerald-50 active:bg-emerald-50"
              >
                <Package className="h-4 w-4 text-emerald-700" />
                <span>{t('nav_my_orders')}</span>
              </Link>

              <Link
                to="/checkout"
                onClick={() => setProfileOpen(false)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-stone-700 hover:bg-emerald-50 active:bg-emerald-50"
              >
                <MapPin className="h-4 w-4 text-emerald-700" />
                <span>{t('nav_addresses')}</span>
              </Link>
            </div>

            <div className="mt-3 border-t border-stone-100 pt-2">
              <button
                type="button"
                onClick={() => {
                  setProfileOpen(false)
                  void signOut()
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 py-2.5 text-xs font-bold text-red-600 hover:bg-red-100 active:scale-98"
              >
                <LogOut className="h-4 w-4 text-red-500" />
                <span>{t('nav_sign_out')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
